import React, { Suspense, lazy, useContext } from 'react';
import { client } from './client/apollo';
import { Route, Switch, Redirect } from 'react-router';
import { BrowserRouter } from 'react-router-dom';
import { ApolloProvider } from 'react-apollo';
// components
import Loader from './components/views/partial/Loader';
import ScrollToTop from './utilities/ScrollToTop';
import InactivityModal from './components/views/partial/InactivityModal/InactivityModal';
// styles
import GlobalStyle from './assets/fonts/fonts';
import { ThemeProvider } from 'styled-components';
import { theme } from './theme';
import './main.css';
import { MainApp } from './AppStyles';
// context
import { ApiDataContextProvider } from './context/ApiDataContext';
import { GlobalContextProvider } from './context/GlobalContext';
import { InStoreContextProvider, InStoreContext } from './context/InStoreContext';
import { PageViewTracking } from './services/analytics';
// hooks
import useInactivityTimeout from './hooks/useInactivityTimeout';
import { useState } from 'react';
import { trackEvent } from './services/analytics';

const Landing = lazy(() => import('./components/views/Landing'));
const Result = lazy(() => import('./containers/ResultsContainer'));

function InactivityWrapper({ children }) {
  const inStoreContext = useContext(InStoreContext);
  const [showInactivityModal, setShowInactivityModal] = useState(false);

  // Inactivity timeout for in-store users who have started consultation
  const shouldMonitorInactivity = inStoreContext?.isInStoreMode && inStoreContext?.hasStartedConsultation;
  
  const { resetTimer: resetInactivityTimer } = useInactivityTimeout(
    60000, // 60 seconds
    () => setShowInactivityModal(true),
    shouldMonitorInactivity && !showInactivityModal
  );

  const handleInactivityContinue = () => {
    setShowInactivityModal(false);
    resetInactivityTimer();
    trackEvent('in_store_inactivity_continue');
  };

  const handleInactivityReset = () => {
    setShowInactivityModal(false);
    trackEvent('in_store_inactivity_reset');
    inStoreContext?.resetInStoreSession();
  };

  return (
    <>
      {children}
      <InactivityModal
        isOpen={showInactivityModal}
        onContinue={handleInactivityContinue}
        onReset={handleInactivityReset}
        countdownDuration={30}
      />
    </>
  );
}

function Routes() {
  return (
    <Suspense fallback={<Loader />}>
      <PageViewTracking />
      <Switch>
        <Route path="/result">
          <ScrollToTop />
          <ApiDataContextProvider>
            <Result />
          </ApiDataContextProvider>
        </Route>
        <Route exact path="/in-store">
          <Landing />
        </Route>
        <Route exact path="/at-home">
          <Landing />
        </Route>
        <Route exact path="/">
          <Landing />
        </Route>
        <Redirect to="/" />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider theme={{ ...theme, fontFamily: 'BootsSharp' }}>
      <MainApp>
        <GlobalContextProvider>
          <ApolloProvider client={client}>
            <InStoreContextProvider>
              <BrowserRouter>
                <InactivityWrapper>
                  <GlobalStyle />
                  <Routes />
                </InactivityWrapper>
              </BrowserRouter>
            </InStoreContextProvider>
          </ApolloProvider>
        </GlobalContextProvider>
      </MainApp>
    </ThemeProvider>
  );
}

export default App;
